import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui';

/**
 * A control for something that cannot be undone.
 *
 * Four actions in this product are irreversible and all four looked exactly
 * like the button an operator presses ten times a day: naming a package closes
 * structuring for good, delivery copies a corpus into a third party's bucket,
 * dropping flagged files removes them from what ships, and composing a
 * replacement company package retires the one a buyer is currently going to
 * receive. Expert operators move by muscle memory, which is the argument for
 * making these two deliberate presses instead of one.
 *
 * The confirmation restates the consequence in the operator's own terms and
 * names the thing being acted on, because "are you sure" asks a question the
 * person has no new information to answer. It arms on a first press, disarms on
 * blur or Escape, and never on a timer: a control that re-arms itself while
 * someone is reading is the same hazard again.
 */
export function Confirm({
  label,
  consequence,
  confirmLabel,
  onConfirm,
  disabled,
  loading,
  busyLabel,
}: {
  /** The resting label, a verb and its object. */
  label: string;
  /** What will happen, stated once armed. Not a question. */
  consequence: string;
  /** The armed label. Says the thing, rather than "Confirm". */
  confirmLabel: string;
  onConfirm: () => void;
  disabled?: boolean;
  loading?: boolean;
  busyLabel?: string;
}) {
  const [armed, setArmed] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  // Focused on arming so the keyboard lands on the confirm rather than an
  // autoFocus attribute, which moves focus without the user asking for it.
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!armed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setArmed(false);
    };
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setArmed(false);
    };
    // The docstring above has always said this disarms on blur, and only
    // mousedown and Escape were handled: a keyboard operator who armed an
    // irreversible control and tabbed away left it armed behind them, which
    // is the hazard the two-press design exists to remove.
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (wrap.current && next && !wrap.current.contains(next)) setArmed(false);
    };
    confirmRef.current?.focus();
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    wrap.current?.addEventListener('focusout', onFocusOut);
    const wrapper = wrap.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      wrapper?.removeEventListener('focusout', onFocusOut);
    };
  }, [armed]);

  // Disarm whenever the action becomes unavailable, so an armed control can
  // never survive into a state that would refuse it.
  useEffect(() => {
    if (disabled) setArmed(false);
  }, [disabled]);

  if (loading) {
    return (
      <Button variant="grave" disabled loading>
        {busyLabel ?? label}
      </Button>
    );
  }

  if (!armed) {
    return (
      <Button variant="grave" disabled={disabled} onClick={() => setArmed(true)}>
        {label}
      </Button>
    );
  }

  return (
    <span ref={wrap} className="inline-flex flex-wrap items-center gap-2">
      <Button
        ref={confirmRef}
        variant="grave"
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
      >
        {confirmLabel}
      </Button>
      <Button variant="ghost" onClick={() => setArmed(false)}>
        Cancel
      </Button>
      <span className="text-caption text-bad">{consequence}</span>
    </span>
  );
}
